import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { getVoyageStatusLabel } from '../views/sales/components/voyage/VoyageStatusUtils';

dayjs.extend(isoWeek);

const parseNumber = (v) => {
  if (v === null || v === undefined) return NaN;
  const cleaned = String(v)
    .replace(/[, ]+/g, '')
    .replace(/[^\d.-]/g, '');
  const n = parseFloat(cleaned);
  return Number.isNaN(n) ? NaN : n;
};

const parseDate = (v) => {
  if (v === null || v === undefined) return NaN;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? NaN : v.getTime();
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  const s = String(v).trim();
  const t = Date.parse(s);
  return Number.isNaN(t) ? NaN : t;
};

const toDateOnlyTs = (timestamp) => {
  if (Number.isNaN(timestamp)) return NaN;
  const d = new Date(timestamp);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const isDateLike = (a, b) => {
  const ta = parseDate(a);
  const tb = parseDate(b);
  return !Number.isNaN(ta) && !Number.isNaN(tb);
};

const matchesFilterItem = (row, item) => {
  if (!item || !item.field) return true;
  const op = item.operator ?? item.operatorValue ?? '';
  const rawValue = item.value;
  let cellValue = row[item.field];

  // Compute virtual status field
  if (item.field === 'status') {
    const actualEtd = row.actualEtd ?? row.actualETD ?? null;
    const actualEta = row.actualEta ?? row.actualETA ?? null;
    const cancelled = row.cancelled ?? false;
    cellValue = getVoyageStatusLabel(
      actualEtd,
      actualEta,
      cancelled,
      row.effectiveShipped
    );
  }

  // Compute virtual calendarWeek field
  if (item.field === 'calendarWeek') {
    const date = row.actualEtd ?? row.initialEtd ?? null;
    if (date == null) {
      cellValue = '';
    } else {
      const d = dayjs(date);
      cellValue = d.isValid() ? d.isoWeek() : '';
    }
  }

  const toStr = (v) =>
    v === null || v === undefined ? '' : String(v).toLowerCase();

  // ── Unary operators (no value required) ──────────────────────
  if (op === 'isEmpty')
    return (
      cellValue === null || cellValue === undefined || toStr(cellValue) === ''
    );
  if (op === 'isNotEmpty')
    return !(
      cellValue === null ||
      cellValue === undefined ||
      toStr(cellValue) === ''
    );

  // For operators that need a value, if none is provided yet treat as "no filter"
  if (rawValue === undefined || rawValue === null || rawValue === '') {
    return true;
  }

  // ── Boolean operator (MUI uses op = "is", value = "true"/"false") ──
  if (
    op === 'is' &&
    (rawValue === 'true' ||
      rawValue === 'false' ||
      rawValue === true ||
      rawValue === false)
  ) {
    const boolVal = rawValue === 'true' || rawValue === true;
    return Boolean(cellValue) === boolVal;
  }

  // ── Date operations ──────────────────────────────────────────
  if (isDateLike(cellValue, rawValue)) {
    const tCell = parseDate(cellValue);
    const tVal = parseDate(rawValue);
    const tCellDate = toDateOnlyTs(tCell);
    const tValDate = toDateOnlyTs(tVal);

    const cmp = (a, b, operatorMap) => {
      switch (operatorMap) {
        case 'equals':
          return a === b;
        case '>':
          return a > b;
        case '<':
          return a < b;
        case '>=':
          return a >= b;
        case '<=':
          return a <= b;
        case 'not':
          return a !== b;
        default:
          return false;
      }
    };

    if (Number.isNaN(tCellDate) || Number.isNaN(tValDate)) {
      if (['equals', '==', '=', 'is'].includes(op))
        return cmp(tCell, tVal, 'equals');
      if (['moreThan', '>', 'after'].includes(op)) return cmp(tCell, tVal, '>');
      if (['lessThan', '<', 'before'].includes(op))
        return cmp(tCell, tVal, '<');
      if (['onOrAfter', '>='].includes(op)) return cmp(tCell, tVal, '>=');
      if (['onOrBefore', '<='].includes(op)) return cmp(tCell, tVal, '<=');
      if (['not', '!='].includes(op)) return cmp(tCell, tVal, 'not');
    } else {
      if (['equals', '==', '=', 'is'].includes(op))
        return cmp(tCellDate, tValDate, 'equals');
      if (['moreThan', '>', 'after'].includes(op))
        return cmp(tCellDate, tValDate, '>');
      if (['lessThan', '<', 'before'].includes(op))
        return cmp(tCellDate, tValDate, '<');
      if (['onOrAfter', '>='].includes(op))
        return cmp(tCellDate, tValDate, '>=');
      if (['onOrBefore', '<='].includes(op))
        return cmp(tCellDate, tValDate, '<=');
      if (['not', '!='].includes(op)) return cmp(tCellDate, tValDate, 'not');
    }
    // fallthrough to string ops for contains etc.
  }

  // ── Numeric handling ─────────────────────────────────────────
  const nCell = parseNumber(cellValue);
  const nVal = parseNumber(rawValue);
  if (!Number.isNaN(nCell) && !Number.isNaN(nVal)) {
    if (['equals', '==', '='].includes(op)) return nCell === nVal;
    if (['!=', 'not'].includes(op)) return nCell !== nVal;
    if (op === '>' || op === 'moreThan') return nCell > nVal;
    if (op === '<' || op === 'lessThan') return nCell < nVal;
    if (op === '>=') return nCell >= nVal;
    if (op === '<=') return nCell <= nVal;
    // fallthrough for contains treated as substring below
  }

  // ── String / fallback operations ─────────────────────────────
  const valStr = toStr(rawValue);
  const cellStr = toStr(cellValue);

  if (['contains', 'containsIgnoreCase'].includes(op))
    return cellStr.includes(valStr);
  if (op === 'startsWith') return cellStr.startsWith(valStr);
  if (op === 'endsWith') return cellStr.endsWith(valStr);
  if (['equals', '==', '=', 'is'].includes(op)) return cellStr === valStr;
  if (['not', '!='].includes(op)) return cellStr !== valStr;

  return cellStr.includes(valStr);
};

export { parseNumber, parseDate, toDateOnlyTs, isDateLike, matchesFilterItem };
